/**
 * useCan — the React-facing capability hook. Subscribes to the effective Set with a selector so a
 * component re-renders only when its specific answer changes (not on every store write).
 *
 *   const canIssue = useCan("finance.facture.issue");
 *   const canAny   = useCanAny(["results.compterendu.read", "results.compterendu.print"]);
 *
 * Semantics mirror the server resolver: effective = preset ∪ grants − denies, deny-wins, the set is
 * server-authoritative. NEVER use the answer as the only guard — the server re-checks every request
 * (this only hides/shows UI and short-circuits routes).
 */
import { useAuthStore } from "./store";

export function useCan(capability: string): boolean {
  return useAuthStore((s) => s.capabilities.has(capability));
}

export function useCanAll(capabilities: ReadonlyArray<string>): boolean {
  return useAuthStore((s) => capabilities.every((c) => s.capabilities.has(c)));
}

export function useCanAny(capabilities: ReadonlyArray<string>): boolean {
  return useAuthStore((s) => capabilities.length === 0 || capabilities.some((c) => s.capabilities.has(c)));
}

/** Imperative variants for non-React call sites (router loaders, command-palette filtering). */
export function can(capability: string): boolean {
  return useAuthStore.getState().can(capability);
}
export function canAll(capabilities: ReadonlyArray<string>): boolean {
  return useAuthStore.getState().canAll(capabilities);
}
